import * as yup from 'yup';
import { getCustomFieldType, validateCustomFieldValue } from 'components/admin/inventory/mapping/customFieldUtils';

/**
 * Generate validation schema for a custom field based on its type
 */
const getCustomFieldSchema = (fieldId: string, fieldConfig: any, t: any) => {
  const result = getCustomFieldType(fieldConfig) as { type: string; format: string; listItems: string[] };
  const { type, format, listItems } = result;

  switch (type) {
    case 'date': {
      return yup.object().shape({
        value: yup
          .string()
          .nullable()
          .test('valid-date', 'Date invalide', (value) => {
            if (!value || value === '') return true; // Optional field
            const validation = validateCustomFieldValue(value, type, format, listItems);
            return (validation as { valid: boolean }).valid;
          }),
        unit: yup.string().nullable(),
      });
    }
    case 'number': {
      const isInteger = format === 'integer';
      return yup.object().shape({
        value: yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) {
              return null;
            }
            return isInteger ? parseInt(originalValue, 10) : parseFloat(originalValue);
          })
          .typeError('Nombre invalide')
          .test('valid-number', 'Nombre invalide', (value) => {
            if (value === null || value === undefined) return true; // Optional field
            return !isNaN(value);
          }),
        unit: yup.string().nullable(),
      });
    }
    case 'list': {
      return yup.object().shape({
        value: yup
          .string()
          .nullable()
          .test('valid-list-item', 'Valeur non autorisée', (value) => {
            if (!value || value === '') return true; // Optional field
            return listItems.includes(value);
          }),
        unit: yup.string().nullable(),
      });
    }
    case 'volume':
    case 'mass': {
      return yup.object().shape({
        value: yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) {
              return null;
            }
            return value;
          })
          .typeError('Valeur numérique requise')
          .test('valid-number', 'Valeur numérique requise', (value) => {
            if (value === null || value === undefined) return true; // Optional field
            return !isNaN(value);
          }),
        unit: yup.string().nullable(),
      });
    }
    case 'boolean': {
      return yup.object().shape({
        value: yup.boolean().nullable(),
        unit: yup.string().nullable(),
      });
    }
    default: {
      // Text fields - no specific validation
      return yup.object().shape({
        value: yup.string().nullable(),
        unit: yup.string().nullable(),
      });
    }
  }
};

export default function validationSchemaWeighed(t: any, mappingConfig?: any) {
  const numericField = (messageKey: string) => yup.object().shape({
    value: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        // Allow empty values
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
          return null;
        }
        return value;
      })
      .typeError(t(messageKey))
      .min(0, t(messageKey)),
    unit: yup.string().nullable(),
  });

  const baseSchema: any = {
    mass: numericField('ullaging.vol_validation'),
    containerMass: numericField('ullaging.vol_validation'),
    volumetricMass: yup.object().shape({
      value: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
          // Allow empty values
          if (originalValue === '' || originalValue === null || originalValue === undefined) {
            return null;
          }
          return value;
        })
        .typeError(t('ullaging.restricted_vol_validation', { volMin: 0, volMax: 999999, volUnit: 'g/L' }))
        .min(0, t('ullaging.restricted_vol_validation', { volMin: 0, volMax: 999999, volUnit: 'g/L' }))
        .max(999999, t('ullaging.restricted_vol_validation', { volMin: 0, volMax: 999999, volUnit: 'g/L' })),
      unit: yup.string().nullable(),
    }),
  };

  // Add validation for custom fields
  if (mappingConfig?.customFields && mappingConfig?.columns) {
    mappingConfig.customFields.forEach((fieldId: string) => {
      const fieldConfig = mappingConfig.columns[fieldId];
      if (fieldConfig) {
        baseSchema[fieldId] = getCustomFieldSchema(fieldId, fieldConfig, t);
      }
    });
  }

  return yup.object().shape(baseSchema);
}
